import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase-auth'
import { generateDeviceHash } from '../lib/device-fingerprint'
import { CheckCircleIcon, XCircleIcon, ArrowPathIcon } from '@heroicons/react/24/solid'

export const VerifyEmail: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [verifying, setVerifying] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  
  const token = searchParams.get('token')
  const type = searchParams.get('type') || 'login'

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Invalid verification link')
        setVerifying(false)
        return
      }

      try {
        // Verify the token
        const { data: verifyData, error: verifyError } = await supabase.functions.invoke('verify-email-token', {
          body: { 
            token,
            type
          }
        })

        if (verifyError || !verifyData?.success) {
          setError(verifyData?.error || 'Invalid or expired verification link')
          setVerifying(false)
          return
        }

        // Success! Check if this is a client verification
        if (type === 'login' && verifyData.isClient) {
          // Register device for future logins
          try {
            const deviceHash = await generateDeviceHash()
            const { data: sessionData } = await supabase.auth.getSession()
            
            if (deviceHash && sessionData?.session) {
              const deviceInfo = {
                userAgent: navigator.userAgent,
                platform: navigator.platform,
                language: navigator.language,
                screenResolution: `${window.screen.width}x${window.screen.height}`,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
              }
              
              await supabase.functions.invoke('check-client-device-verification', {
                body: { 
                  action: 'register',
                  deviceInfo 
                },
                headers: {
                  'Authorization': `Bearer ${sessionData.session.access_token}`,
                  'x-device-hash': deviceHash
                }
              })
            }
          } catch (err) {
            console.error('Failed to register device:', err)
            // Don't block on device registration failure
          }
        }

        // Mark session as verified
        sessionStorage.setItem('mfa_verified', 'true')
        sessionStorage.setItem('email_otp_verified', 'true')
        
        setSuccess(true)
        setVerifying(false)

        // Redirect after short delay
        setTimeout(() => {
          if (verifyData.isClient) {
            navigate('/client-portal/dashboard')
          } else {
            navigate('/portal/clients')
          }
        }, 2000)

      } catch (err) {
        console.error('Verification error:', err)
        setError('An error occurred during verification')
        setVerifying(false)
      }
    }

    verifyToken()
  }, [token, type, navigate])

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 to-blue-800">
        <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full text-center">
          <ArrowPathIcon className="h-16 w-16 text-blue-600 mx-auto mb-4 animate-spin" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying your email...</h2>
          <p className="text-gray-600">Please wait while we confirm your identity</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 to-blue-800">
        <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full text-center">
          <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-3">
            <Link
              to="/client-portal/login"
              className="block w-full py-3 px-4 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg transition-colors"
            >
              Back to Client Login
            </Link>
            <Link
              to="/login"
              className="block w-full py-3 px-4 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors"
            >
              Advisor Login
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 to-blue-800">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full text-center">
        <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h2>
        <p className="text-gray-600 mb-4">Your email has been successfully verified</p>
        <p className="text-sm text-gray-500">Redirecting to your dashboard...</p>
      </div>
    </div>
  )
}